import { Inject, Injectable } from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';

import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { filter } from 'rxjs/internal/operators/filter';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { jwtDecode } from 'jwt-decode';
import { StateService } from '../services/state.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _destroying$ = new Subject<void>();

  private token: string | undefined;

  public userRoles$ = this.stateService.userRoles$;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private stateService: StateService,
  ) {
    this.msalService.handleRedirectObservable().subscribe();

    // Recoge cuentas que esten logados en los eventos de storage (en teoria no haria falta ya que pillariamos la que tendriamos del login)
    this.msalService.instance.enableAccountStorageEvents();
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED,
        ),
      )
      .subscribe((result: EventMessage) => {
        if (this.msalService.instance.getAllAccounts().length === 0) {
          // redirige a la raiz si no tiene ninguna cuenta activa
          window.location.pathname = '/';
        } else {
          // por si queremos hacer algo si estamos logados...
          // this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        // this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });
  }

  // Selecciona una cuenta activa de la lista de cuentas si no tienes una cuenta activa
  checkAndSetActiveAccount() {
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }

    // Ejemplo de obtención de token
    this.msalService
      .acquireTokenSilent({
        scopes: [environment.apiConfig.scopes[0]],
      })
      .subscribe((res) => {
        this.token = res.accessToken;
        sessionStorage.setItem('token', this.token);

        // Decodificar y normalizar los roles del token
        const roles = this.getUserRolesFromToken(this.token);
        this.stateService.setUserRoles(roles);
      });
  }

  /**
   * Decodifica el token JWT y extrae los roles del usuario
   * @param token Token JWT
   * @returns Array de roles del usuario
   */
  getRolesFromToken(token: string): string[] {
    try {
      const decodedToken: any = jwtDecode(token);
      // Los roles en Azure AD pueden venir en diferentes claims:
      // - 'roles': array de roles de aplicación
      // - 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role': roles alternativos
      const roles =
        decodedToken.roles ||
        decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
        [];

      // Si roles es un string, convertirlo a array
      if (typeof roles === 'string') {
        return [roles];
      }

      // Si roles es un array, retornarlo directamente
      return Array.isArray(roles) ? roles : [];
    } catch (error) {
      console.error('Error al decodificar el token:', error);
      return [];
    }
  }

  /**
   * Decodifica y normaliza los roles del token.
   * Reglas:
   * - Mantener `admin` tal cual.
   * - Ignorar `operador` y `coordinador` si vienen sin sufijo.
   * - Convertir `operador_xxx` y `coordinador_xxx` a `operador` y `coordinador`.
   */
  private getUserRolesFromToken(token: string): string[] {
    const rawRoles = this.getRolesFromToken(token) ?? [];
    return this.normalizeRoles(rawRoles);
  }

  private normalizeRoles(roles: string[]): string[] {
    const normalized = roles
      .map((role) => {
        if (role === 'admin') {
          return 'admin';
        }

        if (role.startsWith('operador_')) {
          return 'operador';
        }

        if (role.startsWith('coordinador_')) {
          return 'coordinador';
        }

        // Ignorar roles base sin sufijo
        if (role === 'operador' || role === 'coordinador') {
          return null;
        }

        return role;
      })
      .filter((role): role is string => role !== null);

    return [...new Set(normalized)];
  }

  /**
   * Obtiene los roles del usuario desde el token almacenado
   * @returns Array de roles del usuario
   */
  getUserRoles(): string[] {
    const token = sessionStorage.getItem('token');
    if (token) {
      return this.getUserRolesFromToken(token);
    }
    return this.normalizeRoles(this.stateService.getUserRoles());
  }
}
